import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Eye, EyeOff, Clock, ExternalLink, Save } from "lucide-react";

const STATUS_OPTIONS = [
  { value: "live", label: "Live", desc: "Page renders normally.", color: "border-emerald-700 bg-emerald-950/40 text-emerald-300" },
  { value: "coming_soon", label: "Coming Soon", desc: "Placeholder + Notify-Me capture.", color: "border-amber-700 bg-amber-950/40 text-amber-300" },
  { value: "hidden", label: "Hidden", desc: "Removed from nav, URL 404s.", color: "border-zinc-700 bg-zinc-900 text-zinc-300" },
];

function StatusPill({ status }) {
  const cfg = STATUS_OPTIONS.find((o) => o.value === status) || STATUS_OPTIONS[0];
  return (
    <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-1 border ${cfg.color}`}>
      {cfg.label}
    </span>
  );
}

function PageRow({ page, onSave }) {
  const [draft, setDraft] = useState({
    status: page.status,
    coming_soon_title: page.coming_soon_title,
    coming_soon_body: page.coming_soon_body,
    notify_signup_enabled: page.notify_signup_enabled !== false,
  });
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const dirty =
    draft.status !== page.status ||
    draft.coming_soon_title !== page.coming_soon_title ||
    draft.coming_soon_body !== page.coming_soon_body ||
    draft.notify_signup_enabled !== (page.notify_signup_enabled !== false);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/page-visibility/${page.key}`, draft);
      toast.success(`${page.label} saved`);
      await onSave();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border border-[#27272A] bg-[#0F0F0F]" data-testid={`page-row-${page.key}`}>
      <div className="p-4 md:p-5 flex items-center justify-between flex-wrap gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="font-display uppercase text-lg tracking-tight text-white" data-testid={`page-label-${page.key}`}>{page.label}</div>
            <StatusPill status={draft.status} />
          </div>
          <div className="text-[11px] text-zinc-500 mt-1 flex items-center gap-2">
            <code className="font-mono">{page.path}</code>
            <a
              href={page.path}
              target="_blank"
              rel="noreferrer"
              className="text-zinc-500 hover:text-white inline-flex items-center gap-1"
              data-testid={`page-link-${page.key}`}
            >
              <ExternalLink className="h-3 w-3" /> Open
            </a>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <div className="flex border border-[#27272A] rounded-sm overflow-hidden">
            {STATUS_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setDraft((d) => ({ ...d, status: opt.value }))}
                className={`px-3 py-1.5 text-[10px] uppercase tracking-widest font-bold border-r border-[#27272A] last:border-r-0 ${
                  draft.status === opt.value ? "bg-[#FF331F] text-white" : "bg-[#050505] text-zinc-400 hover:text-white"
                }`}
                data-testid={`status-${opt.value}-${page.key}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="text-[10px] uppercase tracking-widest text-zinc-400 hover:text-white px-3 py-1.5 border border-[#27272A]"
            data-testid={`expand-${page.key}`}
          >
            {expanded ? "Hide copy" : "Edit copy"}
          </button>
          <Button
            onClick={save}
            disabled={!dirty || saving}
            className="bg-[#FF331F] hover:bg-[#FF5242] text-white font-bold uppercase tracking-widest text-xs rounded-sm"
            data-testid={`save-${page.key}`}
          >
            <Save className="h-3 w-3 mr-1" />
            {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>

      {expanded && (
        <div className="border-t border-[#27272A] p-4 md:p-5 space-y-3" data-testid={`expand-section-${page.key}`}>
          <div className="space-y-1.5">
            <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Coming Soon — headline</Label>
            <Input
              value={draft.coming_soon_title}
              onChange={(e) => setDraft((d) => ({ ...d, coming_soon_title: e.target.value }))}
              className="bg-[#050505] border-[#27272A] rounded-sm"
              data-testid={`title-input-${page.key}`}
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Coming Soon — body</Label>
            <Textarea
              value={draft.coming_soon_body}
              onChange={(e) => setDraft((d) => ({ ...d, coming_soon_body: e.target.value }))}
              rows={4}
              className="bg-[#050505] border-[#27272A] rounded-sm"
              data-testid={`body-input-${page.key}`}
            />
          </div>
          <div className="flex items-center justify-between pt-2 border-t border-[#27272A]">
            <div>
              <div className="text-xs uppercase tracking-widest text-zinc-300 font-bold">Notify-me email capture</div>
              <p className="text-[11px] text-zinc-500 mt-0.5">When on, visitors see an email form to be notified when this page is live.</p>
            </div>
            <Switch
              checked={draft.notify_signup_enabled}
              onCheckedChange={(v) => setDraft((d) => ({ ...d, notify_signup_enabled: v }))}
              data-testid={`notify-switch-${page.key}`}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default function PageVisibility() {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/page-visibility");
      setPages(data?.pages || []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const counts = pages.reduce((acc, p) => {
    acc[p.status] = (acc[p.status] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="p-8 md:p-12" data-testid="page-visibility-admin">
      <div className="mb-10 max-w-3xl">
        <div className="text-[#FF331F] text-xs uppercase tracking-[0.3em] font-bold mb-2">Site Control</div>
        <h1 className="font-display uppercase text-4xl md:text-5xl tracking-tight">Page Visibility.</h1>
        <p className="text-zinc-400 text-sm mt-2">
          Flip any public page between <span className="text-emerald-300">Live</span>, <span className="text-amber-300">Coming Soon</span>, and <span className="text-zinc-300">Hidden</span>. Hidden pages disappear from the nav and return a 404. Coming Soon pages show a placeholder + email capture.
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-8 max-w-2xl">
        <div className="border border-emerald-700/50 bg-emerald-950/20 p-3" data-testid="count-live">
          <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-bold flex items-center gap-1"><Eye className="h-3 w-3" /> Live</div>
          <div className="text-2xl font-display mt-1 text-white">{counts.live || 0}</div>
        </div>
        <div className="border border-amber-700/50 bg-amber-950/20 p-3" data-testid="count-coming-soon">
          <div className="text-[10px] uppercase tracking-widest text-amber-300 font-bold flex items-center gap-1"><Clock className="h-3 w-3" /> Coming Soon</div>
          <div className="text-2xl font-display mt-1 text-white">{counts.coming_soon || 0}</div>
        </div>
        <div className="border border-zinc-700 bg-zinc-900/50 p-3" data-testid="count-hidden">
          <div className="text-[10px] uppercase tracking-widest text-zinc-300 font-bold flex items-center gap-1"><EyeOff className="h-3 w-3" /> Hidden</div>
          <div className="text-2xl font-display mt-1 text-white">{counts.hidden || 0}</div>
        </div>
      </div>

      {loading ? (
        <div className="text-zinc-500 text-sm">Loading…</div>
      ) : (
        <div className="space-y-2 max-w-5xl">
          {pages.map((p) => (
            <PageRow key={p.key} page={p} onSave={load} />
          ))}
        </div>
      )}
    </div>
  );
}
